import {
  BLOCKS,
  Document,
  MARKS,
  Node as ContentfulNode,
  Text as ContentfulText
} from '@contentful/rich-text-types'

import { RichText } from './types'
import { EMPTY_VALUE } from './lib'

/**
 * Translation between this editor's slate value and Contentful's rich text
 * `Document`, which is what the CMS stores and what `helpers/CMS.ts` serves
 * from S3.
 *
 * The editor produces a subset of what Contentful allows. Node types it
 * cannot render — heading 4 to 6, quote, table, hyperlink — are flattened to
 * paragraphs when read, so text written in Contentful's own editor still
 * shows up rather than disappearing. Saving afterwards keeps the text and
 * drops that formatting; a document only the studio has touched is unaffected.
 *
 * Text alignment goes the other way: slate carries it, Contentful has no
 * equivalent, and it is dropped on the way out.
 */

const BLOCK_TO_SLATE: Record<string, RichText[number]['type']> = {
  [BLOCKS.PARAGRAPH]: 'paragraph',
  [BLOCKS.HEADING_2]: 'heading-two',
  [BLOCKS.HEADING_3]: 'heading-three',
  [BLOCKS.UL_LIST]: 'bulleted-list',
  [BLOCKS.OL_LIST]: 'numbered-list',
  [BLOCKS.LIST_ITEM]: 'list-item',
  [BLOCKS.HR]: 'hr'
}

const SLATE_TO_BLOCK: Record<string, BLOCKS> = {
  paragraph: BLOCKS.PARAGRAPH,
  'heading-two': BLOCKS.HEADING_2,
  'heading-three': BLOCKS.HEADING_3,
  'bulleted-list': BLOCKS.UL_LIST,
  'numbered-list': BLOCKS.OL_LIST,
  'list-item': BLOCKS.LIST_ITEM,
  hr: BLOCKS.HR
}

type SlateText = {
  text: string
  bold?: boolean
  code?: boolean
  italic?: boolean
  underline?: boolean
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function isSlateText(node: any): node is SlateText {
  return typeof node?.text === 'string'
}

function toContentfulText(node: SlateText): ContentfulText {
  const marks: ContentfulText['marks'] = []

  if (node.bold) {
    marks.push({ type: MARKS.BOLD })
  }

  if (node.italic) {
    marks.push({ type: MARKS.ITALIC })
  }

  if (node.underline) {
    marks.push({ type: MARKS.UNDERLINE })
  }

  if (node.code) {
    marks.push({ type: MARKS.CODE })
  }

  return {
    nodeType: 'text',
    value: node.text,
    marks,
    data: {}
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function toContentfulNode(node: any): ContentfulNode | null {
  if (isSlateText(node)) {
    return toContentfulText(node)
  }

  const nodeType = SLATE_TO_BLOCK[node?.type]

  if (!nodeType) {
    return null
  }

  // A Contentful list item holds blocks, never text, so a slate list item
  // whose children are text needs a paragraph around them.
  const children =
    nodeType === BLOCKS.LIST_ITEM && (node.children || []).some(isSlateText)
      ? [{ type: 'paragraph', children: node.children }]
      : node.children || []

  return {
    nodeType,
    data: {},
    content: children.map(toContentfulNode).filter(Boolean) as ContentfulNode[]
  } as ContentfulNode
}

/**
 * The editor's value as a Contentful document.
 *
 * @param value - Editor value, or null for an untouched field.
 * @returns Document to store, or null when there is nothing to store.
 */
export function toContentfulDocument(value: RichText | null): Document | null {
  if (!value) {
    return null
  }

  const content = value
    .map(toContentfulNode)
    .filter(Boolean) as Document['content']

  return {
    nodeType: BLOCKS.DOCUMENT,
    data: {},
    content
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function toSlateText(node: ContentfulText): any {
  const marks = node.marks.map((mark) => mark.type)

  return {
    text: node.value,
    ...(marks.includes(MARKS.BOLD) && { bold: true }),
    ...(marks.includes(MARKS.ITALIC) && { italic: true }),
    ...(marks.includes(MARKS.UNDERLINE) && { underline: true }),
    ...(marks.includes(MARKS.CODE) && { code: true })
  }
}

/**
 * The text of a node the editor has no element for, so nothing is lost when a
 * document written in Contentful is opened here.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function flattenToText(node: any): SlateText[] {
  if (node.nodeType === 'text') {
    return [toSlateText(node)]
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (node.content || []).flatMap((child: any) => flattenToText(child))
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function toSlateNode(node: any): any {
  if (node.nodeType === 'text') {
    return toSlateText(node)
  }

  const type = BLOCK_TO_SLATE[node.nodeType]

  if (!type) {
    const text = flattenToText(node)

    return {
      type: 'paragraph',
      children: text.length > 0 ? text : [{ text: '' }]
    }
  }

  // The paragraph a list item needs in Contentful is noise in slate, so it is
  // unwrapped again — the round trip is stable.
  const content =
    type === 'list-item'
      ? (node.content || []).flatMap(
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (child: any) =>
            child.nodeType === BLOCKS.PARAGRAPH ? child.content || [] : [child]
        )
      : node.content || []

  const children = content.map(toSlateNode)

  return {
    type,
    // Slate requires at least one child, and an empty paragraph or an hr has
    // no content in Contentful.
    children: children.length > 0 ? children : [{ text: '' }]
  }
}

/**
 * A stored document as an editor value.
 *
 * @param document - Document from the CMS, or null when the field is empty.
 * @returns Editor value; an empty paragraph when there is nothing stored.
 */
export function fromContentfulDocument(
  document: Document | null | undefined
): RichText {
  if (!document || document.content.length === 0) {
    return EMPTY_VALUE
  }

  return document.content.map(toSlateNode)
}
